
import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import {
  ArrowLeft,
  ArrowRight,
  TestTube,
  Save,
  X,
  Trash2,
  AlertCircle,
  MessageSquare,
  Mail,
  Calendar,
  Video,
  FileText,
  Headphones,
} from "lucide-react"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Textarea } from "./ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Checkbox } from "./ui/checkbox"
import { Slider } from "./ui/slider"
import { Badge } from "./ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs"
import { Switch } from "./ui/switch"
import { Alert, AlertDescription } from "./ui/alert"
import { getTeamNames, getOrgEqTracker } from "../../api/helpers"

export interface TrackerBuilderProps {
  trackerId?: string | null
  tracker?: any
  onSave: (trackerData: TrackerData) => void
  onCancel: () => void
  isLoading?: boolean
}

interface TrackerData {
  name: string
  description: string
  visibility: "private" | "team" | "organization"
  sources: string[]
  teams: string[]
  slackChannels: string[]
  slackDmsEnabled: boolean
  zoom1on1Enabled: boolean
  keywords: string[]
  phrases: string[]
  regex: string
  semanticTopics: string[]
  semanticThreshold: number
  participantRoles: string[]
  meetingTypes: string[]
  minDuration: number
  emotions: Array<{ name: string; threshold: number }>
  valenceRange: [number, number]
  arousalRange: [number, number]
  timeWindowEnabled: boolean
  timeWindowMinutes: number
  timeWindowAggregate: "mean" | "max" | "p95"
  notificationInApp: boolean
  notificationEmail: boolean
  notificationSlack: boolean
  taggingTarget: string
  taggingName: string
  webhookUrl: string
  webhookSecret: string
  cooldownSeconds: number
  timingEnabled: boolean
  startDate: string
  endDate: string
  eventDate: string
}

const initialData: TrackerData = {
  name: "",
  description: "",
  visibility: "private",
  sources: [],
  teams: [],
  slackChannels: [],
  slackDmsEnabled: false,
  zoom1on1Enabled: false,
  keywords: [],
  phrases: [],
  regex: "",
  semanticTopics: [],
  semanticThreshold: 0.7,
  participantRoles: [],
  meetingTypes: ["sales", "demo", "discovery", "checkin", "standup", "retro", "oneonone", "allhands", "training"],
  minDuration: 0,
  emotions: [],
  valenceRange: [-1, 1],
  arousalRange: [0, 1],
  timeWindowEnabled: false,
  timeWindowMinutes: 30,
  timeWindowAggregate: "mean",
  notificationInApp: true,
  notificationEmail: false,
  notificationSlack: false,
  taggingTarget: "",
  taggingName: "",
  webhookUrl: "",
  webhookSecret: "",
  cooldownSeconds: 300,
  timingEnabled: false,
  startDate: "",
  endDate: "",
  eventDate: "",
}

const availableSources = [
  { id: "slack", label: "Slack", description: "Team communication channels", icon: MessageSquare },
  { id: "gmail", label: "Gmail", description: "Email communications", icon: Mail },
  { id: "zoom", label: "Zoom", description: "Video call transcripts", icon: Video },
]

// Teams will be fetched dynamically from the API
const defaultAvailableTeams = [
  { id: "engineering", label: "Engineering", description: "Software development team" },
  { id: "product", label: "Product", description: "Product management team" },
  { id: "design", label: "Design", description: "Design and UX team" },
  { id: "sales", label: "Sales", description: "Sales team" },
  { id: "marketing", label: "Marketing", description: "Marketing team" },
  { id: "customer-success", label: "Customer Success", description: "Customer support and success" },
  { id: "operations", label: "Operations", description: "Operations team" },
  { id: "finance", label: "Finance", description: "Finance and accounting" },
  { id: "hr", label: "Human Resources", description: "HR and people operations" },
  { id: "executive", label: "Executive", description: "Leadership team" },
]

const humeEmotions = [
  "Admiration",
  "Adoration",
  "Aesthetic Appreciation",
  "Amusement",
  "Anger",
  "Anxiety",
  "Awe",
  "Awkwardness",
  "Boredom",
  "Calmness",
  "Concentration",
  "Confusion",
  "Contemplation",
  "Contempt",
  "Contentment",
  "Craving",
  "Desire",
  "Determination",
  "Disappointment",
  "Disgust",
  "Distress",
  "Doubt",
  "Ecstasy",
  "Embarrassment",
  "Empathic Pain",
  "Entrancement",
  "Envy",
  "Excitement",
  "Fear",
  "Guilt",
  "Horror",
  "Interest",
  "Joy",
  "Love",
  "Nostalgia",
  "Pain",
  "Pride",
  "Realization",
  "Relief",
  "Romance",
  "Sadness",
  "Satisfaction",
  "Shame",
  "Surprise (negative)",
  "Surprise (positive)",
  "Sympathy",
  "Tiredness",
  "Triumph",
]

const meetingTypeOptions = [
  { id: "standup", label: "Daily Standup" },
  { id: "planning", label: "Sprint Planning" },
  { id: "review", label: "Review Meeting" },
  { id: "sales", label: "Sales Call" },
  { id: "support", label: "Support Call" },
  { id: "oneonone", label: "1:1 Meeting" },
  { id: "allhands", label: "All Hands" },
  { id: "training", label: "Training Session" },
]

export function TrackerBuilder({ trackerId, tracker, onSave, onCancel, isLoading = false }: TrackerBuilderProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [data, setData] = useState<TrackerData>(initialData)
  const [testResult, setTestResult] = useState<any>(null)
  const [showSlackConsent, setShowSlackConsent] = useState(false)
  const [showZoomConsent, setShowZoomConsent] = useState(false)
  const [availableTeams, setAvailableTeams] = useState<Array<{ id: string; label: string; description: string }>>(defaultAvailableTeams)
  const [teamsLoading, setTeamsLoading] = useState(false)

  // Get user from Redux (same pattern as emotion-trackers-list and work-topic-snapshot)
  const user = useSelector((state: any) => state.auth.user)

  // Fetch and pre-populate full tracker data when editing
  useEffect(() => {
    const fetchAndPopulateTracker = async () => {
      if (trackerId && user?.organization_info?._id) {
        try {
          console.log("TrackerBuilder: Fetching full tracker details for ID:", trackerId)
          
          // Fetch the complete tracker data from the API
          const response = await getOrgEqTracker(user.organization_info._id, trackerId)
          const fullTracker = response?.tracker || response
          
          console.log("TrackerBuilder: Full tracker data received:", fullTracker)
          console.log("TrackerBuilder: All tracker keys:", Object.keys(fullTracker || {}))
          
          if (fullTracker) {
            // Transform API data to form format
            const sources: string[] = []
            if (fullTracker.data_sources) {
              fullTracker.data_sources.forEach((ds: string) => {
                if (ds === "chats") sources.push("slack")
                else if (ds === "emails") sources.push("gmail")
                else if (ds === "calls") sources.push("zoom")
              })
            } else if (fullTracker.sources) {
              // Fallback to sources field if data_sources not available
              sources.push(...fullTracker.sources)
            }

            // Map visibility from API format to form format
            let visibility: "private" | "team" | "organization" = "private"
            if (fullTracker.visibility === "public") {
              visibility = "organization"
            } else if (fullTracker.visibility === "team") {
              visibility = "team"
            } else if (fullTracker.visibility === "private") {
              visibility = "private"
            }
            console.log(`TrackerBuilder: Visibility mapping - API: "${fullTracker.visibility}" -> Form: "${visibility}"`)

            const populatedData: TrackerData = {
              name: fullTracker.tracker_name || fullTracker.name || "",
              description: fullTracker.description || "",
              visibility: visibility,
              sources: sources,
              teams: fullTracker.org_team_ids || fullTracker.teams || [],
              slackChannels: fullTracker.slack_channels || [],
              slackDmsEnabled: fullTracker.slack_dms_enabled || false,
              zoom1on1Enabled: fullTracker.zoom_1on1_enabled || false,
              keywords: fullTracker.keywords || [],
              phrases: fullTracker.phrases || [],
              regex: fullTracker.regex || "",
              semanticTopics: fullTracker.semantic_topics || [],
              semanticThreshold: fullTracker.semantic_threshold || 0.7,
              participantRoles: fullTracker.participant_roles || [],
              meetingTypes: fullTracker.meeting_types || ["sales", "demo", "discovery", "checkin", "standup", "retro", "oneonone", "allhands", "training"],
              minDuration: fullTracker.min_duration || 0,
              emotions: fullTracker.emotions || [],
              valenceRange: fullTracker.valence_range || [-1, 1],
              arousalRange: fullTracker.arousal_range || [0, 1],
              timeWindowEnabled: fullTracker.time_window_enabled || false,
              timeWindowMinutes: fullTracker.time_window_minutes || 30,
              timeWindowAggregate: fullTracker.time_window_aggregate || "mean",
              notificationInApp: !!fullTracker.notifications?.includes("in-app"),
              notificationEmail: !!fullTracker.notifications?.includes("email"),
              notificationSlack: !!fullTracker.notifications?.includes("slack"),
              taggingTarget: fullTracker.tagging_target || "",
              taggingName: fullTracker.tagging_name || "",
              webhookUrl: fullTracker.webhook_url || "",
              webhookSecret: fullTracker.webhook_secret || "",
              cooldownSeconds: fullTracker.notification_cooldown || fullTracker.cooldown_seconds || 300,
              timingEnabled: !!(fullTracker.start_date || fullTracker.end_date || fullTracker.event_date),
              startDate: fullTracker.start_date ? fullTracker.start_date.split('T')[0] : "",
              endDate: fullTracker.end_date ? fullTracker.end_date.split('T')[0] : "",
              eventDate: fullTracker.event_date ? fullTracker.event_date.split('T')[0] : "",
            }

            setData(populatedData)
            console.log("TrackerBuilder: Form populated with:", populatedData)
            console.log("TrackerBuilder: Notifications from API:", fullTracker.notifications)
            console.log("TrackerBuilder: Notification toggles - InApp:", populatedData.notificationInApp, "Email:", populatedData.notificationEmail, "Slack:", populatedData.notificationSlack)
            console.log("TrackerBuilder: Dates - Start:", populatedData.startDate, "End:", populatedData.endDate, "Event:", populatedData.eventDate, "TimingEnabled:", populatedData.timingEnabled)
          }
        } catch (error) {
          console.error("TrackerBuilder: Error fetching tracker details:", error)
          // If fetch fails and we have the tracker from props, use that as fallback
          if (tracker) {
            console.log("TrackerBuilder: Using fallback tracker from props")
            // Use the existing logic with the passed tracker
          }
        }
      } else {
        // Reset to initial data when creating new tracker
        console.log("TrackerBuilder: Creating new tracker, using initial data")
        setData(initialData)
      }
    }

    fetchAndPopulateTracker()
  }, [trackerId, user?.organization_info?._id])

  // Fetch teams when user/organization is available (matches tracker fetching pattern)
  useEffect(() => {
    const fetchTeams = async () => {
      if (!user?.organization_info?._id) {
        console.warn("⚠️ TrackerBuilder - No organization ID available, using default teams")
        return
      }

      setTeamsLoading(true)
      try {
        console.log("🔍 TrackerBuilder - Fetching teams for organization:", user.organization_info._id)
        const response = await getTeamNames(user.organization_info._id)
        console.log("🔍 TrackerBuilder - Teams API response:", response)
        
        // Response structure: { success: true, team_names: [{team_id, team_name}], count: N }
        if (response?.success && response?.team_names && Array.isArray(response.team_names)) {
          if (response.team_names.length === 0) {
            console.warn("⚠️ TrackerBuilder - No teams exist, using default teams")
          } else {
            // Map API response to component format
            const formattedTeams = response.team_names.map((team: any) => ({
              id: team.team_id,
              label: team.team_name,
              description: `${team.team_name} team`
            }))
            setAvailableTeams(formattedTeams)
            console.log(`✅ TrackerBuilder - Successfully loaded ${formattedTeams.length} teams from API`)
          }
        } else {
          console.warn("⚠️ TrackerBuilder - Invalid response, using default teams")
        }
      } catch (error) {
        console.error("❌ TrackerBuilder - Error fetching teams:", error)
        console.warn("⚠️ TrackerBuilder - Using default teams as fallback")
      } finally {
        setTeamsLoading(false)
      }
    }

    fetchTeams()
  }, [user?.organization_info?._id])

  const steps = [
    { id: 1, title: "Definition", description: "Name and visibility" },
    { id: 2, title: "Sources", description: "Data sources" },
    { id: 3, title: "Actions", description: "What to do when matched" },
    { id: 4, title: "Live Preview", description: "Test and review" },
  ]

  const updateData = (updates: Partial<TrackerData>) => {
    setData((prev) => ({ ...prev, ...updates }))
  }

  const addKeyword = (keyword: string) => {
    if (keyword && !data.keywords.includes(keyword)) {
      updateData({ keywords: [...data.keywords, keyword] })
    }
  }

  const removeKeyword = (keyword: string) => {
    updateData({ keywords: data.keywords.filter((k) => k !== keyword) })
  }


  const addEmotion = (emotion: string) => {
    if (!data.emotions.find((e) => e.name === emotion)) {
      updateData({ emotions: [...data.emotions, { name: emotion, threshold: 0.5 }] })
    }
  }

  const updateEmotionThreshold = (emotion: string, threshold: number) => {
    updateData({
      emotions: data.emotions.map((e) => (e.name === emotion ? { ...e, threshold } : e)),
    })
  }

  const removeEmotion = (emotion: string) => {
    updateData({ emotions: data.emotions.filter((e) => e.name !== emotion) })
  }

  const handleSourceToggle = (sourceId: string) => {
    const newSources = data.sources.includes(sourceId)
      ? data.sources.filter((s) => s !== sourceId)
      : [...data.sources, sourceId]

    updateData({ sources: newSources })

    if (sourceId === "slack" && newSources.includes("slack")) {
      setShowSlackConsent(true)
    }

    if (sourceId === "zoom" && newSources.includes("zoom")) {
      setShowZoomConsent(true)
    }
  }

  const handleTeamToggle = (teamId: string) => {
    const newTeams = data.teams.includes(teamId) ? data.teams.filter((t) => t !== teamId) : [...data.teams, teamId]
    updateData({ teams: newTeams })
  }

  const testTracker = async () => {
    // Mock test result
    setTestResult({
      wouldMatch: true,
      triggeredRules: ["Confusion ≥ 0.35", "keyword: pricing"],
      sampleText: "I'm really confused about the pricing structure...",
    })
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return data.name.trim() !== ""
      case 2:
        return data.sources.length > 0
      case 3:
        return true
      case 4:
        return true
      default:
        return false
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Tracker Name</Label>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => updateData({ name: e.target.value })}
                placeholder="e.g., Product Launch Reactions"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={data.description}
                onChange={(e) => updateData({ description: e.target.value })}
                placeholder="Describe what this tracker monitors..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Keywords</Label>
              <p className="text-xs text-muted-foreground">Exact words or phrases to match in text content</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Add keyword..."
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      addKeyword(e.currentTarget.value)
                      e.currentTarget.value = ""
                    }
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {data.keywords.map((keyword) => (
                  <Badge key={keyword} variant="secondary" className="flex items-center gap-1">
                    {keyword}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeKeyword(keyword)} />
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Label className="text-base">Timing</Label>
                  <Switch
                    checked={data.timingEnabled}
                    onCheckedChange={(checked: boolean) => updateData({ timingEnabled: checked })}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.timingEnabled
                    ? "Define when this tracker should be active"
                    : "If disabled, the tracker will run continuously"}
                </p>
              </div>

              {data.timingEnabled && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="startDate">Start Date</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={data.startDate}
                        onChange={(e) => updateData({ startDate: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="endDate">End Date</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={data.endDate}
                        onChange={(e) => updateData({ endDate: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="eventDate">Event Date (Optional)</Label>
                    <p className="text-xs text-muted-foreground">
                      Use this for tracking against a specific event-based initiative
                    </p>
                    <Input
                      id="eventDate"
                      type="date"
                      value={data.eventDate}
                      onChange={(e) => updateData({ eventDate: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="visibility">Visibility</Label>
              <Select value={data.visibility} onValueChange={(value: any) => updateData({ visibility: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[9999] bg-white text-black border border-gray-200 shadow-lg">
                  <SelectItem value="private" className="text-black hover:bg-gray-100 cursor-pointer">Private - Only you can see this</SelectItem>
                  <SelectItem value="team" className="text-black hover:bg-gray-100 cursor-pointer">Team - Your team can see this</SelectItem>
                  <SelectItem value="organization" className="text-black hover:bg-gray-100 cursor-pointer">Organization - Everyone can see this</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-4">Select Data Sources</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableSources.map((source) => (
                  <Card
                    key={source.id}
                    className={`cursor-pointer transition-all ${
                      data.sources.includes(source.id)
                        ? "border-primary bg-primary/5 shadow-md"
                        : "hover:shadow-md hover:border-muted-foreground/50"
                    }`}
                    onClick={() => handleSourceToggle(source.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          checked={data.sources.includes(source.id)}
                          onCheckedChange={() => handleSourceToggle(source.id)}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                        <div className="flex-shrink-0">
                          {React.createElement(source.icon, { className: "h-5 w-5 text-muted-foreground" })}
                        </div>
                        <div>
                          <div className="font-medium">{source.label}</div>
                          <div className="text-sm text-muted-foreground">{source.description}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {showSlackConsent && data.sources.includes("slack") && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  By enabling Slack, you allow analysis of messages in selected channels to detect emotional and topical
                  signals. Direct Messages are off by default and require explicit opt-in.
                  <div className="mt-3 flex items-center space-x-2">
                    <Switch
                      checked={data.slackDmsEnabled}
                      onCheckedChange={(checked: boolean) => updateData({ slackDmsEnabled: checked })}
                    />
                    <Label>Include Direct Messages</Label>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {showZoomConsent && data.sources.includes("zoom") && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  By enabling Zoom, you allow analysis of meeting transcripts to detect emotional and topical signals.
                  1:1 meetings are excluded by default for privacy.
                  <div className="mt-3 flex items-center space-x-2">
                    <Switch
                      checked={data.zoom1on1Enabled}
                      onCheckedChange={(checked: boolean) => updateData({ zoom1on1Enabled: checked })}
                    />
                    <Label>Include 1:1 Meetings</Label>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div>
              <h3 className="text-lg font-medium mb-2">Select Teams</h3>
              <p className="text-sm text-muted-foreground mb-4">Choose which teams this tracker should monitor</p>
              {teamsLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading teams...
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availableTeams.map((team) => (
                    <div
                      key={team.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors duration-200 ease-in-out hover:bg-muted/50 ${
                        data.teams.includes(team.id)
                          ? "border-2 border-primary bg-primary/5"
                          : "border border-border bg-card"
                      }`}
                      onClick={() => handleTeamToggle(team.id)}
                    >
                      <Checkbox
                        checked={data.teams.includes(team.id)}
                        onCheckedChange={() => handleTeamToggle(team.id)}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                      <div>
                        <div className="font-medium text-sm">{team.label}</div>
                        <div className="text-xs text-muted-foreground">{team.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-4">Notifications</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={data.notificationInApp}
                    onCheckedChange={(checked: boolean) => updateData({ notificationInApp: !!checked })}
                  />
                  <Label>In-app notifications</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={data.notificationEmail}
                    onCheckedChange={(checked: boolean) => updateData({ notificationEmail: !!checked })}
                  />
                  <Label>Email notifications</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={data.notificationSlack}
                    onCheckedChange={(checked: boolean) => updateData({ notificationSlack: !!checked })}
                  />
                  <Label>Slack DM notifications</Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cooldown Period (seconds)</Label>
              <Input
                type="number"
                value={data.cooldownSeconds}
                onChange={(e) => updateData({ cooldownSeconds: Number.parseInt(e.target.value) || 0 })}
              />
              <div className="text-xs text-muted-foreground">
                Minimum time between notifications for the same tracker
              </div>
            </div>

            <div className="space-y-2">
              <Label>Webhook URL (optional)</Label>
              <Input
                value={data.webhookUrl}
                onChange={(e) => updateData({ webhookUrl: e.target.value })}
                placeholder="https://your-webhook-url.com"
              />
            </div>
          </div>
        )

      case 4:
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <Button onClick={testTracker} className="w-full">
                <TestTube className="h-4 w-4 mr-2" />
                Test on Sample Data
              </Button>

              {testResult && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                  <div className="text-sm font-medium">
                    Would Match:{" "}
                    <span className={testResult.wouldMatch ? "text-green-600" : "text-red-600"}>
                      {testResult.wouldMatch ? "Yes" : "No"}
                    </span>
                  </div>
                  {testResult.triggeredRules && (
                    <div className="text-sm">
                      <div className="font-medium mb-2">Triggered Rules:</div>
                      <div className="space-y-1">
                        {testResult.triggeredRules.map((rule: string, index: number) => (
                          <Badge key={index} variant="secondary">
                            {rule}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {testResult.sampleText && (
                    <div className="text-sm">
                      <div className="font-medium mb-2">Sample Text:</div>
                      <div className="p-3 bg-background border rounded italic">"{testResult.sampleText}"</div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium">Tracker Summary</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Name:</span> {data.name || "Untitled Tracker"}
                  </div>
                  <div>
                    <span className="font-medium">Description:</span> {data.description || "No description"}
                  </div>
                  <div>
                    <span className="font-medium">Sources:</span>{" "}
                    {data.sources.length > 0 ? (
                      <div className="inline-flex items-center gap-2 ml-2">
                        {data.sources.map((sourceId) => {
                          const source = availableSources.find((s) => s.id === sourceId)
                          if (!source) return null
                          return (
                            <div key={sourceId} className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md">
                              {React.createElement(source.icon, { className: "h-4 w-4" })}
                              <span className="text-xs">{source.label}</span>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      "None selected"
                    )}
                  </div>
                  <div>
                    <span className="font-medium">Keywords:</span>{" "}
                    {data.keywords.length > 0 ? data.keywords.join(", ") : "None"}
                  </div>
                  <div>
                    <span className="font-medium">Emotions:</span>{" "}
                    {data.emotions.length > 0 ? data.emotions.map((e) => e.name).join(", ") : "None"}
                  </div>
                  <div>
                    <span className="font-medium">Meeting Types:</span>{" "}
                    {data.meetingTypes.length > 0
                      ? meetingTypeOptions
                          .filter((option) => data.meetingTypes.includes(option.id))
                          .map((option) => option.label)
                          .join(", ")
                      : "None selected"}
                  </div>
                  {data.teams.length > 0 && (
                    <div>
                      <span className="font-medium">Teams:</span>{" "}
                      {data.teams.map(teamId => {
                        const team = availableTeams.find(t => t.id === teamId)
                        return team?.label
                      }).filter(Boolean).join(", ")}
                    </div>
                  )}
                  {data.timingEnabled && (
                    <div>
                      <span className="font-medium">Timing:</span>{" "}
                      {data.startDate && data.endDate
                        ? `${data.startDate} to ${data.endDate}`
                        : "Dates not set"}
                      {data.eventDate && ` (Event: ${data.eventDate})`}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Notifications:</span>{" "}
                    {[
                      data.notificationInApp && "In-app",
                      data.notificationEmail && "Email",
                      data.notificationSlack && "Slack",
                    ]
                      .filter(Boolean)
                      .join(", ") || "None"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  const toggleMeetingType = (meetingTypeId: string) => {
    const newMeetingTypes = data.meetingTypes.includes(meetingTypeId)
      ? data.meetingTypes.filter((type) => type !== meetingTypeId)
      : [...data.meetingTypes, meetingTypeId]
    updateData({ meetingTypes: newMeetingTypes })
  }

  const toggleAllMeetings = () => {
    const allSelected = data.meetingTypes.length === meetingTypeOptions.length
    updateData({
      meetingTypes: allSelected ? [] : meetingTypeOptions.map((option) => option.id),
    })
  }

  const addSemanticTopic = (topic: string) => {
    if (topic && !data.semanticTopics.includes(topic)) {
      updateData({ semanticTopics: [...data.semanticTopics, topic] })
    }
  }

  const removeSemanticTopic = (topic: string) => {
    updateData({ semanticTopics: data.semanticTopics.filter((t) => t !== topic) })
  }


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onCancel}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{trackerId ? "Edit Tracker" : "Create New Tracker"}</h1>
            <p className="text-muted-foreground">
              Step {currentStep} of {steps.length}: {steps[currentStep - 1]?.title}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center space-x-2 py-4">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep === step.id
                  ? "bg-primary text-primary-foreground"
                  : currentStep > step.id
                    ? "bg-green-500 text-white"
                    : "bg-muted text-muted-foreground"
              }`}
            >
              {currentStep > step.id ? "✓" : step.id}
            </div>
            <div className="ml-2 text-sm">
              <div className={`font-medium ${currentStep === step.id ? "text-foreground" : "text-muted-foreground"}`}>
                {step.title}
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className={`w-8 h-px mx-4 ${currentStep > step.id ? "bg-green-500" : "bg-muted"}`} />
            )}
          </div>
        ))}
      </div>

      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>{steps[currentStep - 1]?.title}</CardTitle>
          </CardHeader>
          <CardContent>{renderStep()}</CardContent>
        </Card>

        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
          >
            Previous
          </Button>

          <div className="flex gap-2">
            {currentStep === steps.length ? (
              <Button onClick={() => onSave(data)} disabled={!canProceed() || isLoading}>
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? "Saving..." : "Save Tracker"}
              </Button>
            ) : (
              <Button onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))} disabled={!canProceed()}>
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
